import { auth } from './firebaseAuth';
import { useEffect } from 'react';
import { USERS_COLLECTION } from '../shared/variables/collections';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  clearUser,
  setAuthReady,
  setHasOffer,
  setPartnerInviteCount,
  setUser,
  setUserData,
} from '../store/slices/userSlice';
import { setConfig, selectConfig } from '../store/slices/configSlice';
import { clearAuthSession, persistAuthSession } from './authSession';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const AUTH_INITIALIZATION_TIMEOUT_MS = 10_000;

const CONFIG_DEFAULTS: RemoteConfig.ConfigValues = {
  footer_download_app_icons_enabled: true,
  google_login_button_enabled: true,
  delete_account_enabled: false,
};

export function useUserData() {
  const dispatch = useAppDispatch();

  useEffect(() => {
    let active = true;
    let authSettled = false;
    const initializationTimeout = setTimeout(() => {
      if (!active || authSettled) return;
      // Ağ, eklenti veya tarayıcı depolama sorunu giriş ekranını sonsuza dek
      // kilitlememeli. Listener daha sonra gelirse state yine güncellenir.
      dispatch(setAuthReady(true));
    }, AUTH_INITIALIZATION_TIMEOUT_MS);

    const unsubscribe = auth.onIdTokenChanged(
      user => {
        (async () => {
          if (!user) {
            clearAuthSession();
            if (!active) return;
            authSettled = true;
            clearTimeout(initializationTimeout);
            dispatch(clearUser());
            dispatch(setAuthReady(true));
            return;
          }

          try {
            await persistAuthSession(user);
          } catch (error) {
            console.error('Auth token could not be persisted', error);
            if (!active) return;
            authSettled = true;
            clearTimeout(initializationTimeout);
            dispatch(clearUser());
            dispatch(setAuthReady(true));
            return;
          }

          if (!active) return;

          authSettled = true;
          clearTimeout(initializationTimeout);
          dispatch(
            setUser({
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              photoURL: user.photoURL,
              emailVerified: user.emailVerified,
            }),
          );
          dispatch(setUserData(null));
          dispatch(setAuthReady(true));

          try {
            // Firestore SDK'sı yalnız giriş yapmış kullanıcı için iner; anonim ziyaretçinin
            // her sayfada yüklediği `_app` paketinde yer almaz.
            const [
              { getMyOffers, getMyPartnerInvites, getUserByUid },
              { firestore, timestamp, doc, updateDoc },
            ] = await Promise.all([import('./firestoreClient'), import('./firestoreActions')]);
            const userDoc: Users.User[] = await getUserByUid(user.uid);
            if (!active) return;

            if (userDoc?.length) {
              const userInfo: Users.User = userDoc[0];
              dispatch(setUserData(userInfo));

              try {
                const myOffers = await getMyOffers(userInfo?.id);
                if (active) dispatch(setHasOffer(!!myOffers?.length));
              } catch {
                if (active) dispatch(setHasOffer(false));
              }

              try {
                const partnerInvites = await getMyPartnerInvites(userInfo?.id);
                if (active) dispatch(setPartnerInviteCount(partnerInvites?.length || 0));
              } catch {
                if (active) dispatch(setPartnerInviteCount(0));
              }

              // `onIdTokenChanged` her sayfa yüklemesinde ve saatlik token yenilemesinde
              // tetikleniyor. Her seferinde yazmak, `kullanici` koleksiyonunu dinleyen
              // her admin oturumuna okuma olarak faturalanıyordu. Günde bir kez yeterli.
              const lastLoginMs =
                typeof (userInfo.last_login as any)?.toMillis === 'function'
                  ? (userInfo.last_login as any).toMillis()
                  : Number(userInfo.last_login) || 0;

              if (Date.now() - lastLoginMs > ONE_DAY_MS) {
                await updateDoc(doc(firestore, USERS_COLLECTION, userInfo.id), {
                  last_login: timestamp(),
                }).catch(() => undefined);
              }
            }
          } catch (error) {
            // Firebase Auth oturumu geçerlidir; yardımcı profil sorgusundaki bir hata
            // kullanıcının uygulamada çıkış yapmış görünmesine neden olmamalı.
            console.error('User profile could not be loaded', error);
          }
        })().catch(error => {
          console.error('Auth state could not be synchronized', error);
          if (active) {
            authSettled = true;
            clearTimeout(initializationTimeout);
            dispatch(setAuthReady(true));
          }
        });
      },
      error => {
        console.error('Firebase auth listener failed', error);
        if (!active) return;
        authSettled = true;
        clearTimeout(initializationTimeout);
        clearAuthSession();
        dispatch(clearUser());
        dispatch(setAuthReady(true));
      },
    );

    return () => {
      active = false;
      clearTimeout(initializationTimeout);
      unsubscribe();
    };
  }, []);
}

export function useRemoteConfig() {
  const values = useAppSelector(selectConfig);

  const get = (key: keyof RemoteConfig.ConfigValues): boolean => values[key];

  return { get };
}

export function useProvideRemoteConfig() {
  const dispatch = useAppDispatch();

  useEffect(() => {
    async function initRemoteConfig() {
      try {
        // remote-config yalnızca burada gerekiyor; dinamik import ana bundle'ı küçük tutar
        const { fetchAndActivate, getBoolean, getRemoteConfig } = await import(
          'firebase/remote-config'
        );
        const remoteConfig = getRemoteConfig();
        remoteConfig.defaultConfig = { ...CONFIG_DEFAULTS };
        if (process.env.NODE_ENV !== 'production') {
          remoteConfig.settings.minimumFetchIntervalMillis = 15000;
        }
        await fetchAndActivate(remoteConfig);

        dispatch(
          setConfig({
            footer_download_app_icons_enabled: getBoolean(
              remoteConfig,
              'footer_download_app_icons_enabled',
            ),
            google_login_button_enabled: getBoolean(remoteConfig, 'google_login_button_enabled'),
            delete_account_enabled: getBoolean(remoteConfig, 'delete_account_enabled'),
          }),
        );
      } catch (e) {
        console.warn('Remote config init failed, using defaults', e);
      }
    }

    initRemoteConfig();
  }, []);
}
